import React, { createContext, useContext, useEffect } from "react";
import { useQuery, gql } from "@apollo/client";
import capitalize from "lodash/capitalize";
import { getAuthToken } from "../apollo/client";

const DEFAULT_STATE = {
  loggedIn: false,
  user: undefined,
  loading: false,
  error: undefined,
};

const AuthContext = createContext(DEFAULT_STATE);

export const GET_USER = gql`
  query getUser {
    viewer {
      id
      customerId
      databaseId
      billingAddress1
      billingAddress2
      billingCity
      billingCompany
      billingCountry
      billingFirstName
      billingLastName
      billingPostcode
      billingState
      billingPhone
      firstName
      lastName
      email
      orders {
        id
        orderNumber
        shippingTotal
        total
        date
        status
        lineItems {
          nodes {
            id
            quantity
            total
            product {
              id
              title
              image
              productTitle
              attributes {
                name
                value
              }
            }
          }
        }
      }
      shippingAddress1
      shippingAddress2
      shippingCity
      shippingCompany
      shippingCountry
      shippingFirstName
      shippingLastName
      shippingPostcode
      shippingState
      shippingPhone
    }
  }
`;

const structureLineItem = (lineItem) => ({
  productId: lineItem?.product?.id ?? lineItem?.productId ?? null,
  image: lineItem?.product?.image ?? null,
  title: lineItem?.product?.productTitle ?? '',
  variation: [...lineItem?.product?.attributes]
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map(({ value }) => `${capitalize(value)}`)
        .join(', ') ?? '',
  quantity: lineItem?.quantity ?? 0,
  total: lineItem?.total ?? '0.00',
});

const structureOrder = (order) => ({
  id: order?.id ?? null,
  orderNumber: order?.orderNumber ?? null,
  shippingTotal: order?.shippingTotal ?? '0.00',
  total: order?.total ?? '0.00',
  date: order?.date ?? null,
  status: order?.status ?? 'unknown',
  lineItems: order?.lineItems?.nodes?.map(structureLineItem) ?? [],
});

const structureUser = (viewer) => {
  if (!viewer) return null;

  const orders = Array.isArray(viewer.orders)
    ? viewer.orders
    : viewer.orders?.nodes ?? [];

  return {
    id: viewer.id ?? null,
    customerId: viewer.customerId ?? null,
    databaseId: viewer.databaseId ?? null,
    firstName: viewer.firstName ?? '',
    lastName: viewer.lastName ?? '',
    email: viewer.email ?? '',
    billingPhone: viewer.billingPhone ?? '',
    billingAddress1: viewer.billingAddress1 ?? '',
    billingAddress2: viewer.billingAddress2 ?? '',
    billingCity: viewer.billingCity ?? '',
    billingCompany: viewer.billingCompany ?? '',
    billingCountry: viewer.billingCountry ?? '',
    billingPostcode: viewer.billingPostcode ?? '',
    billingState: viewer.billingState ?? '',
    shipping: {
      firstName: viewer.shippingFirstName ?? '',
      lastName: viewer.shippingLastName ?? '',
      company: viewer.shippingCompany ?? '',
      address1: viewer.shippingAddress1 ?? '',
      address2: viewer.shippingAddress2 ?? '',
      city: viewer.shippingCity ?? '',
      state: viewer.shippingState ?? '',
      postcode: viewer.shippingPostcode ?? '',
      country: viewer.shippingCountry ?? '',
      phone: viewer.shippingPhone ?? '',
    },
    orders: orders.map(structureOrder),
  };
};

export function AuthProvider({ children }) {
  const { data, loading, error, refetch } = useQuery(GET_USER);
  const user = structureUser(data?.viewer);
  const loggedIn = Boolean(user);

  useEffect(() => {
    const hasAuthToken = Boolean(getAuthToken());

    if (!hasAuthToken || user || loading) {
      return undefined;
    }

    let isCancelled = false;
    let retryTimeout = null;
    const maxRetries = 3;
    const retryDelayMs = 400;

    const recoverViewerFromToken = async (attempt = 1) => {
      try {
        const response = await refetch();
        const hasViewer = Boolean(response?.data?.viewer?.id);

        if (!hasViewer && !isCancelled && attempt < maxRetries) {
          retryTimeout = setTimeout(() => {
            void recoverViewerFromToken(attempt + 1);
          }, retryDelayMs);
        }
      } catch {
        if (!isCancelled && attempt < maxRetries) {
          retryTimeout = setTimeout(() => {
            void recoverViewerFromToken(attempt + 1);
          }, retryDelayMs);
        }
      }
    };

    void recoverViewerFromToken();

    return () => {
      isCancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [loading, refetch, user]);
  
  const value = {
    loggedIn,
    user,
    loading,
    error,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => useContext(AuthContext);

export default useAuth;
